/**
 * Text as a person reads it: the casing of what they typed, the monogram an
 * avatar falls back to, and the one way a name is composed for them to read.
 */
/**
 * A word carrying no lowercase letter — the shouted paste this repairs. It is
 * tested against the word PAST ITS FIRST CHARACTER, because the first character
 * is force-capitalized either way: judging the whole word reads "jOHN" (a shift
 * key held a beat too long) as cased by hand and leaves "JOHN", which a second
 * pass then reads as shouted and returns as "John". A blur handler that writes
 * its result back to the field runs on every blur, and a value that depends on
 * how many times the field was left is not a rule.
 */
const SHOUTED = /^\P{Ll}*$/u;
/**
 * Inside a word being un-shouted, the letter after an apostrophe or hyphen that
 * has another letter behind it: the O'Brien and Mary-Jane positions. Requiring
 * that following letter is what leaves a possessive alone — the "s" of
 * "Smith's" is the last letter in the word.
 */
const INNER_SEGMENT = /(['’\-])(\p{L})(?=\p{L})/gu;
/**
 * The first character of a word, as a character and not as a UTF-16 unit: an
 * astral letter is one letter, and splitting it in half leaves two halves of
 * nothing.
 */
function firstCharacter(word) {
    const codePoint = word.codePointAt(0);
    return codePoint === undefined ? "" : String.fromCodePoint(codePoint);
}
/**
 * The word with its first character capitalized — unless capitalizing it makes
 * it two characters. `"ß".toUpperCase()` is "SS" and `"ﬁ".toUpperCase()` is
 * "FI": that is a respelling rather than a capital, and the letter it spills
 * into the body of the word reads as a shout the next time the field is
 * blurred, which would give one field two answers.
 */
function capitalizeFirst(word) {
    const first = firstCharacter(word);
    const upper = first.toUpperCase();
    return upper.length === first.length ? upper + word.slice(first.length) : word;
}
function capitalizeWord(word) {
    const rest = word.slice(firstCharacter(word).length);
    // Only a shouted word is lowercased. A word the person cased themselves is
    // left alone below the first letter, because "McDonald" and "DeLuca" carry
    // capitals that a blanket lowercase would delete — and a name field is the
    // last place to correct someone's spelling of their own name.
    const body = SHOUTED.test(rest)
        ? word
            .toLowerCase()
            .replace(INNER_SEGMENT, (_match, separator, letter) => separator + letter.toUpperCase())
        : word;
    return capitalizeFirst(body);
}
/**
 * Each space-separated word starting with a capital.
 *
 * | input            | output           |
 * | ---------------- | ---------------- |
 * | `john smith`     | `John Smith`     |
 * | `JOHN SMITH`     | `John Smith`     |
 * | `jOHN`           | `John`           |
 * | `JOHN O'BRIEN`   | `John O'Brien`   |
 * | `MARY-JANE`      | `Mary-Jane`      |
 * | `SMITH'S FARM`   | `Smith's Farm`   |
 * | `McDonald`       | `McDonald`       |
 * | `o'brien`        | `O'brien`        |
 *
 * The last two rows are the rule: a word the person cased themselves keeps its
 * casing below the first letter, and only a shouted word is rebuilt.
 *
 * The result is a fixed point — running it over its own output changes nothing
 * — because this is the blur handler on a name or address field, where the
 * value a person typed in a hurry needs to match the one already on file and
 * the handler writes what it returns back into the field.
 *
 * Nullish and empty input gives `""`.
 */
export function capitalizeWords(value) {
    if (!value)
        return "";
    return value.split(" ").map(capitalizeWord).join(" ");
}
/**
 * The monogram an avatar falls back to: the first character of each of the
 * first two words, upper case.
 *
 * | input             | output |
 * | ----------------- | ------ |
 * | `Chris Hardin`    | `CH`   |
 * | `chris`           | `C`    |
 * | `Ana De La Cruz`  | `AD`   |
 * | `  Ana  Cruz  `   | `AC`   |
 * | `` / nullish      | `?`    |
 *
 * Two letters, never three: the circle it renders in is sized for two, and a
 * third letter either overflows it or shrinks the type below what the rest of
 * the surface reads at. Words are separated by any run of whitespace, because
 * a name pasted out of a spreadsheet arrives carrying a tab or a double space
 * and "C" is not the monogram for "Chris  Hardin".
 *
 * A name with no letters in it — and an empty or nullish one — gives "?", so
 * the avatar always renders something rather than collapsing to an empty
 * circle.
 */
export function initials(name) {
    const monogram = (name ?? "")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => firstCharacter(word).toUpperCase())
        .join("");
    return monogram || "?";
}
/**
 * The name a person reads for someone: first and last.
 *
 * The middle name belongs to the legal form (the LPOA, a contract, a broker
 * removal request), which the server keeps on `name`, so a label never carries
 * it. An AKA and a broker's own record of a person are identity data, not
 * labels, and are spelled out in full where they are shown.
 *
 * The components win over a label the server composed, because that label can
 * still spell a middle name. A component that is only whitespace counts as none,
 * so the label stands in for it. Runs of whitespace collapse, so a person invited
 * before they have a name, whose `name` is a single space, reads as `""`.
 */
export function personDisplayName(person) {
    if (!person)
        return "";
    const composed = [person.first_name, person.last_name]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(" ");
    return (composed || person.display_name || person.name || "").replace(/\s+/g, " ").trim();
}
/**
 * Someone known by the first name and masked address they gave (`j***@example.com`):
 * the first name with the address beside it, which is how two people with one
 * first name are told apart, or the address alone where they gave no first
 * name. A blank value counts as none.
 */
export function maskedPersonNames(person) {
    const name = person.first_name?.trim() || null;
    const email = person.masked_email?.trim() || null;
    return { name, email: name ? email : null, label: name ?? email };
}
